import React, { useState, useEffect } from 'react';
import { obtenerHistorialPagos, obtenerEgresos } from '../../services/FinanzasService';
import {
    FileText,
    DollarSign,
    PieChart,
    BarChart2,
    Download
} from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import 'react-toastify/dist/ReactToastify.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const styles = StyleSheet.create({
    page: {
        padding: 30,
        backgroundColor: '#FFFFFF',
        fontFamily: 'Helvetica'
    },
    header: {
        marginBottom: 20,
        borderBottom: 1,
        paddingBottom: 10,
        borderBottomColor: '#4F46E5'
    },
    title: {
        fontSize: 20,
        color: '#1E293B',
        textAlign: 'center',
        fontWeight: 'bold'
    },
    subtitle: {
        fontSize: 14,
        marginTop: 14,
        marginBottom: 6,
        color: '#334155',
        fontWeight: 'bold',
        borderBottom: 0.5,
        borderBottomColor: '#E2E8F0',
        paddingBottom: 3
    },
    sectionTitle: {
        fontSize: 14,
        color: '#1E293B',
        fontWeight: 'bold',
        marginBottom: 10,
        backgroundColor: '#F1F5F9',
        padding: 6,
        borderRadius: 4,
        textAlign: 'left'
    },
    sectionNumber: {
        backgroundColor: '#4F46E5',
        color: 'white',
        fontSize: 10,
        fontWeight: 'bold',
        padding: 4,
        borderRadius: 12,
        marginRight: 8,
        width: 18,
        height: 18,
        textAlign: 'center'
    },
    section: {
        marginBottom: 20
    },
    row: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 8,
        alignItems: 'center'
    },
    column: {
        flex: 1,
        marginHorizontal: 5
    },
    summaryBox: {
        padding: 10,
        backgroundColor: '#F8FAFC',
        borderRadius: 6,
        marginBottom: 7,
        borderLeft: 3,
        borderLeftColor: '#4F46E5'
    },
    summaryBoxPositive: {
        borderLeftColor: '#10B981' // Verde para valores positivos
    },
    summaryBoxNegative: {
        borderLeftColor: '#EF4444' // Rojo para valores negativos
    },
    label: {
        fontSize: 9,
        color: '#64748B',
        marginBottom: 3
    },
    value: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#1E293B'
    },
    valuePositive: {
        color: '#10B981' // Verde para valores positivos
    },
    valueNegative: {
        color: '#EF4444' // Rojo para valores negativos
    },
    table: {
        display: 'table',
        width: '100%',
        borderRadius: 6,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: '#E5E7EB',
        marginTop: 8
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomColor: '#E2E8F0',
        borderBottomWidth: 1,
        minHeight: 24,
        alignItems: 'center'
    },
    tableHeader: {
        backgroundColor: '#F1F5F9',
        fontWeight: 'bold'
    },
    tableCell: {
        flex: 1,
        padding: 6,
        fontSize: 9
    },
    tableCellHeader: {
        color: '#1E293B',
        fontSize: 9,
        fontWeight: 'bold'
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 30,
        right: 30,
        textAlign: 'center',
        color: '#94A3B8',
        fontSize: 9,
        borderTop: 1,
        borderTopColor: '#E2E8F0',
        paddingTop: 10
    },
    divider: {
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
        marginVertical: 15
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10
    },
    headerLogo: {
        width: 50,
        height: 50
    },
    dateInfo: {
        fontSize: 10,
        color: '#64748B',
        textAlign: 'right',
        marginBottom: 5
    },
    periodBadge: {
        backgroundColor: '#EBF5FF',
        padding: 6,
        borderRadius: 4,
        alignSelf: 'center',
        marginBottom: 10
    },
    periodText: {
        fontSize: 10,
        color: '#3B82F6',
        fontWeight: 'bold'
    },
    subheader: {
        fontSize: 12,
        color: '#64748B',
        textAlign: 'center',
        marginBottom: 15
    },
    chart: {
        width: '100%',
        height: 150,
        marginVertical: 10
    },
    categorySummary: {
        backgroundColor: '#F8FAFC',
        borderRadius: 6,
        padding: 10,
        marginVertical: 5
    },
    categoryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginVertical: 4
    },
    pill: {
        paddingVertical: 4,
        paddingHorizontal: 8,
        borderRadius: 12,
        fontSize: 8,
        textAlign: 'center',
        fontWeight: 'bold'
    },
    warningPill: {
        backgroundColor: '#FEF3C7',
        color: '#B45309'
    },
    toc: {
        marginBottom: 15
    },
    tocItem: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 6
    },
    tocNumber: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#4F46E5',
        marginRight: 5
    },
    tocText: {
        fontSize: 10,
        color: '#1E293B'
    },
    tocLine: {
        flex: 1,
        height: 1,
        backgroundColor: '#E2E8F0',
        marginHorizontal: 5
    },
    tocPage: {
        fontSize: 10,
        color: '#64748B'
    },
    cardContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between'
    },
    card: {
        width: '48%',
        backgroundColor: '#F8FAFC',
        borderRadius: 6,
        padding: 10,
        marginBottom: 10,
        borderTop: 3,
        borderTopColor: '#4F46E5'
    },
    cardPositive: {
        borderTopColor: '#10B981'
    },
    cardNegative: {
        borderTopColor: '#EF4444'
    },
    cardTitle: {
        fontSize: 9,
        color: '#64748B',
        marginBottom: 5
    },
    cardValue: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#1E293B'
    },
    pageNumber: {
        position: 'absolute',
        bottom: 20,
        right: 30,
        fontSize: 9,
        color: '#94A3B8'
    },
    sectionIcon: {
        height: 14,
        width: 14,
        marginRight: 5
    }
});

// Un componente para mostrar porcentajes en formato barra
const BarPercentage = ({ value, maxValue, color }) => (
    <View style={{ width: '100%', height: 4, backgroundColor: '#E2E8F0', marginTop: 5, borderRadius: 2 }}>
        <View 
            style={{
                width: `${(value / maxValue) * 100}%`,
                height: 4,
                backgroundColor: color || '#4F46E5',
                borderRadius: 2
            }}
        />
    </View>
);

// Componente para encabezado de sección
const SectionHeader = ({ number, title }) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
        <Text style={styles.sectionNumber}>{number}</Text>
        <Text style={styles.sectionTitle}>{title}</Text>
    </View>
);

const ReportePDF = ({ data }) => {
    const currentDate = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Calcular totales para barras de porcentaje
    const totalIngresos = Object.values(data.resumenMensual.distribucionIngresos).reduce((a, b) => a + b, 0);
    const totalEgresos = Object.values(data.resumenMensual.distribucionEgresos).reduce((a, b) => a + b, 0);
    
    // Calcular total de egresos pendientes
    const totalEgresosPendientes = data.egresosPendientes.reduce((sum, egreso) => sum + parseFloat(egreso.monto), 0);
    
    // Análisis financiero
    const balance = data.resumenMensual.totalIngresos - data.resumenMensual.totalEgresos;
    const tieneBalancePositivo = balance >= 0;
    
    return (
    <Document>
        <Page size="A4" style={styles.page}>
                {/* ----- ENCABEZADO PRINCIPAL ----- */}
            <View style={styles.header}>
                    <View style={styles.headerRow}>
                        <View>
                            <Text style={styles.title}>Reporte Financiero</Text>
                        </View>
            </View>

                    <View style={styles.periodBadge}>
                        <Text style={styles.periodText}>
                            {data.tipoPeriodo === 'mes' 
                                ? `${data.mesSeleccionado} ${data.anioSeleccionado}` 
                                : `Año Completo ${data.anioSeleccionado}`}
                            </Text>
                        </View>
                    
                    <Text style={styles.subheader}>
                        Reporte generado el {currentDate}
                    </Text>
                    </View>

                {/* ----- ÍNDICE DEL REPORTE ----- */}
                <View style={styles.toc}>
                    <Text style={[styles.subtitle, { marginBottom: 10 }]}>Contenido</Text>
                    
                    <View style={styles.tocItem}>
                        <Text style={styles.tocNumber}>1.</Text>
                        <Text style={styles.tocText}>Resumen Financiero</Text>
                        <View style={styles.tocLine} />
                        <Text style={styles.tocPage}>1</Text>
                    </View>
                    
                    <View style={styles.tocItem}>
                        <Text style={styles.tocNumber}>2.</Text>
                        <Text style={styles.tocText}>Análisis de Ingresos</Text>
                        <View style={styles.tocLine} />
                        <Text style={styles.tocPage}>1</Text>
                    </View>
                    
                    <View style={styles.tocItem}>
                        <Text style={styles.tocNumber}>3.</Text>
                        <Text style={styles.tocText}>Análisis de Egresos</Text>
                        <View style={styles.tocLine} />
                        <Text style={styles.tocPage}>1</Text>
                    </View>
                    
                    <View style={styles.tocItem}>
                        <Text style={styles.tocNumber}>4.</Text>
                        <Text style={styles.tocText}>Egresos Pendientes</Text>
                        <View style={styles.tocLine} />
                        <Text style={styles.tocPage}>1</Text>
                    </View>
                    
                    <View style={styles.tocItem}>
                        <Text style={styles.tocNumber}>5.</Text>
                        <Text style={styles.tocText}>Conclusiones y Recomendaciones</Text>
                        <View style={styles.tocLine} />
                        <Text style={styles.tocPage}>1</Text>
                    </View>
                </View>
                
                <View style={styles.divider} />

                {/* ----- 1. RESUMEN FINANCIERO ----- */}
                <View style={styles.section}>
                    <SectionHeader number="1" title="Resumen Financiero" />
                    
                    <View style={styles.cardContainer}>
                        <View style={[styles.card, styles.cardPositive]}>
                            <Text style={styles.cardTitle}>INGRESOS TOTALES</Text>
                            <Text style={[styles.cardValue, styles.valuePositive]}>
                                ${totalIngresos.toFixed(2)}
                            </Text>
                        </View>
                        
                        <View style={[styles.card, styles.cardNegative]}>
                            <Text style={styles.cardTitle}>EGRESOS TOTALES</Text>
                            <Text style={[styles.cardValue, styles.valueNegative]}>
                                ${totalEgresos.toFixed(2)}
                            </Text>
                    </View>
                        
                        <View style={[styles.card, tieneBalancePositivo ? styles.cardPositive : styles.cardNegative]}>
                            <Text style={styles.cardTitle}>BALANCE</Text>
                            <Text style={[styles.cardValue, tieneBalancePositivo ? styles.valuePositive : styles.valueNegative]}>
                                ${balance.toFixed(2)}
                            </Text>
                        </View>
                        
                        <View style={[styles.card, data.egresosPendientes.length > 0 ? styles.cardNegative : styles.cardPositive]}>
                            <Text style={styles.cardTitle}>EGRESOS PENDIENTES</Text>
                            <Text style={[styles.cardValue, data.egresosPendientes.length > 0 ? styles.valueNegative : styles.valuePositive]}>
                                ${totalEgresosPendientes.toFixed(2)}
                            </Text>
                    </View>
                </View>
                    
                    {/* Indicador de salud financiera */}
                    <View style={[styles.categorySummary, { marginTop: 10 }]}>
                        <Text style={[styles.value, tieneBalancePositivo ? styles.valuePositive : styles.valueNegative]}>
                            {tieneBalancePositivo 
                                ? "✓ Balance Positivo" 
                                : "✗ Balance Negativo"}
                        </Text>
                        
                        {!tieneBalancePositivo && (
                            <View style={{ marginTop: 5 }}>
                                <Text style={[styles.label, styles.warningPill]}>
                                    Atención: Los egresos superan a los ingresos por ${Math.abs(balance).toFixed(2)}
                                </Text>
                            </View>
                        )}
                </View>
            </View>

            <View style={styles.divider} />

                {/* ----- 2. ANÁLISIS DE INGRESOS ----- */}
            <View style={styles.section}>
                    <SectionHeader number="2" title="Análisis de Ingresos" />
                    
                    {Object.entries(data.resumenMensual.distribucionIngresos).length > 0 ? (
                        <>
                            {Object.entries(data.resumenMensual.distribucionIngresos)
                                .sort(([, a], [, b]) => b - a) // Ordenar por monto descendente
                                .map(([tipo, monto], index) => (
                                    <View key={tipo} style={styles.categorySummary}>
                                        <View style={styles.categoryRow}>
                                <Text style={styles.label}>{tipo}</Text>
                                            <Text style={[styles.value, styles.valuePositive]}>${monto.toFixed(2)}</Text>
                                        </View>
                                        <Text style={[styles.label, { marginTop: 2 }]}>
                                            {((monto / totalIngresos) * 100).toFixed(1)}% del total
                                        </Text>
                                        <BarPercentage 
                                            value={monto}
                                            maxValue={totalIngresos}
                                            color="#10B981"
                                        />
                            </View>
                        ))}
                        </>
                    ) : (
                        <Text style={styles.label}>No hay datos de ingresos para este período.</Text>
                    )}
                    </View>

                <View style={styles.divider} />

                {/* ----- 3. ANÁLISIS DE EGRESOS ----- */}
                <View style={styles.section}>
                    <SectionHeader number="3" title="Análisis de Egresos" />
                    
                    {Object.entries(data.resumenMensual.distribucionEgresos).length > 0 ? (
                        <>
                            {Object.entries(data.resumenMensual.distribucionEgresos)
                                .sort(([, a], [, b]) => b - a) // Ordenar por monto descendente
                                .map(([tipo, monto], index) => (
                                    <View key={tipo} style={styles.categorySummary}>
                                        <View style={styles.categoryRow}>
                                <Text style={styles.label}>{tipo}</Text>
                                            <Text style={[styles.value, styles.valueNegative]}>${monto.toFixed(2)}</Text>
                                        </View>
                                        <Text style={[styles.label, { marginTop: 2 }]}>
                                            {((monto / totalEgresos) * 100).toFixed(1)}% del total
                                        </Text>
                                        <BarPercentage 
                                            value={monto}
                                            maxValue={totalEgresos}
                                            color="#EF4444"
                                        />
                            </View>
                        ))}
                        </>
                    ) : (
                        <Text style={styles.label}>No hay datos de egresos para este período.</Text>
                    )}
            </View>

            <View style={styles.divider} />

                {/* ----- 4. EGRESOS PENDIENTES ----- */}
            <View style={styles.section}>
                    <SectionHeader number="4" title="Egresos Pendientes" />
                    
                    {data.egresosPendientes.length > 0 ? (
                        <>
                            <View style={[styles.categorySummary, { marginBottom: 10 }]}>
                                <Text style={styles.label}>Total de Egresos Pendientes</Text>
                                <Text style={[styles.value, styles.valueNegative]}>
                                    ${totalEgresosPendientes.toFixed(2)}
                                </Text>
                                <Text style={[styles.label, { marginTop: 5 }]}>
                                    {data.egresosPendientes.length} pagos pendientes en total
                                </Text>
                            </View>
                            
                <View style={styles.table}>
                    <View style={[styles.tableRow, styles.tableHeader]}>
                                    <Text style={[styles.tableCellHeader, { flex: 2 }]}>Concepto</Text>
                                    <Text style={styles.tableCellHeader}>Monto</Text>
                                    <Text style={styles.tableCellHeader}>Tipo</Text>
                                    <Text style={styles.tableCellHeader}>Estado</Text>
                    </View>
                                
                                {data.egresosPendientes.slice(0, 6).map((egreso) => (
                        <View key={egreso.id_egreso} style={styles.tableRow}>
                            <Text style={[styles.tableCell, { flex: 2 }]}>{egreso.concepto}</Text>
                            <Text style={styles.tableCell}>${parseFloat(egreso.monto).toFixed(2)}</Text>
                            <Text style={styles.tableCell}>{egreso.tipo_egreso}</Text>
                                        <Text style={styles.tableCell}>
                                            <Text style={[styles.pill, { backgroundColor: '#FEF3C7', color: '#B45309' }]}>Pendiente</Text>
                                        </Text>
                        </View>
                    ))}
                </View>
                        </>
                    ) : (
                        <View style={[styles.categorySummary, { borderLeftColor: '#10B981' }]}>
                            <Text style={[styles.value, styles.valuePositive]}>No hay egresos pendientes</Text>
                            <Text style={styles.label}>Todos los pagos están al día</Text>
                        </View>
                    )}
            </View>

                <View style={styles.divider} />

                {/* ----- 5. CONCLUSIONES Y RECOMENDACIONES ----- */}
                <View style={styles.section}>
                    <SectionHeader number="5" title="Conclusiones y Recomendaciones" />
                    
                    <View style={styles.categorySummary}>
                        <Text style={[styles.label, { marginBottom: 5, fontSize: 10, fontWeight: 'bold' }]}>Resumen del Período</Text>
                        
                        {tieneBalancePositivo ? (
                            <Text style={styles.label}>
                                El período muestra un balance positivo, con ingresos que superan a los egresos
                                por ${balance.toFixed(2)}, lo que representa un {((balance / totalIngresos) * 100).toFixed(1)}% de los ingresos totales.
                            </Text>
                        ) : (
                            <Text style={styles.label}>
                                El período muestra un balance negativo, con egresos que superan a los ingresos
                                por ${Math.abs(balance).toFixed(2)}, lo que representa un {((Math.abs(balance) / totalIngresos) * 100).toFixed(1)}% de déficit.
                            </Text>
                        )}
                        
                        {data.egresosPendientes.length > 0 && (
                            <Text style={[styles.label, { marginTop: 5 }]}>
                                Existen {data.egresosPendientes.length} egresos pendientes por un total de ${totalEgresosPendientes.toFixed(2)}.
                                Se recomienda revisarlos y programar su pago según prioridad.
                            </Text>
                        )}
                    </View>
                    
                    <View style={[styles.categorySummary, { marginTop: 10 }]}>
                        <Text style={[styles.label, { marginBottom: 5, fontSize: 10, fontWeight: 'bold' }]}>Recomendaciones</Text>
                        
                        {tieneBalancePositivo ? (
                            <Text style={styles.label}>
                                • Considerar reinvertir el excedente en mejoras para la institución.{'\n'}
                                • Mantener la estrategia actual de gestión financiera.{'\n'}
                                • Evaluar opciones para optimizar los ingresos en las categorías de menor rendimiento.
                            </Text>
                        ) : (
                            <Text style={styles.label}>
                                • Revisar y ajustar los gastos en las categorías de mayor egreso.{'\n'}
                                • Explorar estrategias para incrementar los ingresos.{'\n'}
                                • Crear un plan de acción para balancear el presupuesto.
                            </Text>
                        )}
                    </View>
                </View>

                {/* Pie de página y número de página */}
            <View style={styles.footer}>
                    <Text>Sistema de Gestión Financiera | Centro Infantil</Text>
            </View>
                
                <Text style={styles.pageNumber}>Página 1 de 1</Text>
        </Page>
    </Document>
);
};

const Button = ({ children, onClick, className = '', variant = 'primary', size = 'default' }) => {
    const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';

    const variantStyles = {
        primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
        green: 'bg-emerald-500 text-white hover:bg-emerald-600'
    };

    const sizeStyles = {
        default: 'px-4 py-2 text-sm',
        sm: 'px-3 py-1.5 text-xs'
    };

    return (
        <button
            onClick={onClick}
            className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
        >
            {children}
        </button>
    );
};

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        }
    });
};

const ReportesFinanzas = () => {
    const [ingresos, setIngresos] = useState([]);
    const [egresos, setEgresos] = useState([]);
    const [mesSeleccionado, setMesSeleccionado] = useState(new Date().getMonth() + 1);
    const [anioSeleccionado, setAnioSeleccionado] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [tipoPeriodo, setTipoPeriodo] = useState('mes'); // 'mes' o 'anio'
    const [mesesDisponibles, setMesesDisponibles] = useState([]);
    const [aniosDisponibles, setAniosDisponibles] = useState([]);

    useEffect(() => {
        cargarDatos();
    }, [tipoPeriodo]);
    
    // Efecto para actualizar el mes y año seleccionado cuando cambian los disponibles
    useEffect(() => {
        if (mesesDisponibles.length > 0 && !mesesDisponibles.includes(mesSeleccionado)) {
            setMesSeleccionado(mesesDisponibles[0]);
        }
    }, [mesesDisponibles, mesSeleccionado]);
    
    useEffect(() => {
        if (aniosDisponibles.length > 0 && !aniosDisponibles.includes(anioSeleccionado)) {
            setAnioSeleccionado(aniosDisponibles[0]);
        }
    }, [aniosDisponibles, anioSeleccionado]);
    
    // Efecto para cargar datos cuando cambia el mes o año seleccionado
    useEffect(() => {
        if (!loading) {
            filtrarDatosPorPeriodo();
        }
    }, [mesSeleccionado, anioSeleccionado, loading]);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [ingresosData, egresosData] = await Promise.all([
                obtenerHistorialPagos(),
                obtenerEgresos()
            ]);

            const ingresosArray = ingresosData.historialPagos || [];
            const egresosArray = egresosData.egresos || [];
            
            setIngresos(ingresosArray);
            setEgresos(egresosArray);
            
            // Calcular meses y años disponibles
            calcularPeriodosDisponibles(ingresosArray, egresosArray);
        } catch (error) {
            showErrorToast('Error al obtener datos de la base de datos');
        } finally {
            setLoading(false);
        }
    };
    
    // Función para calcular los meses y años que tienen datos
    const calcularPeriodosDisponibles = (ingresosArray, egresosArray) => {
        // Conjunto para almacenar meses y años únicos
        const mesesSet = new Set();
        const aniosSet = new Set();
        
        // Procesar ingresos
        ingresosArray.forEach(ingreso => {
            // Intentar usar fecha_pago
            if (ingreso.fecha_pago) {
                try {
                    const fecha = new Date(ingreso.fecha_pago);
                    const mes = fecha.getMonth() + 1;
                    const anio = fecha.getFullYear();
                    
                    // Guardar combinación mes-año
                    mesesSet.add(`${mes}-${anio}`);
                    aniosSet.add(anio);
                } catch (error) {
                    // Si hay un error al parsear la fecha, usar mes_pagado y anio_pagado
                    if (ingreso.mes_pagado && ingreso.anio_pagado) {
                        mesesSet.add(`${ingreso.mes_pagado}-${ingreso.anio_pagado}`);
                        aniosSet.add(ingreso.anio_pagado);
                    }
                }
            } 
            // Si no hay fecha_pago, usar mes_pagado y anio_pagado
            else if (ingreso.mes_pagado && ingreso.anio_pagado) {
                mesesSet.add(`${ingreso.mes_pagado}-${ingreso.anio_pagado}`);
                aniosSet.add(ingreso.anio_pagado);
            }
        });
        
        // Procesar egresos
        egresosArray.forEach(egreso => {
            const fecha = new Date(egreso.fecha_pago);
            const mes = fecha.getMonth() + 1;
            const anio = fecha.getFullYear();
            
            // Guardar combinación mes-año
            mesesSet.add(`${mes}-${anio}`);
            aniosSet.add(anio);
        });
        
        // Convertir a arrays y ordenar
        const aniosArray = Array.from(aniosSet).sort((a, b) => b - a); // Ordenar descendente
        
        // Actualizar estados
        setAniosDisponibles(aniosArray.length > 0 ? aniosArray : [new Date().getFullYear()]);
        
        // Filtrar meses disponibles para el año seleccionado
        actualizarMesesDisponibles(mesesSet, anioSeleccionado);
    };
    
    // Actualizar meses disponibles cuando cambia el año seleccionado
    const actualizarMesesDisponibles = (mesesSet, anio) => {
        if (!mesesSet) return;
        
        const mesesDelAnio = Array.from(mesesSet)
            .filter(item => item.endsWith(`-${anio}`))
            .map(item => parseInt(item.split('-')[0]))
            .sort((a, b) => a - b); // Ordenar ascendente
        
        setMesesDisponibles(mesesDelAnio.length > 0 ? mesesDelAnio : [new Date().getMonth() + 1]);
    };
    
    // Efecto para actualizar meses disponibles cuando cambia el año seleccionado
    useEffect(() => {
        if (!loading) {
            const mesesSet = new Set();
            
            // Recopilar meses con datos para el año seleccionado
            ingresos.forEach(ingreso => {
                // Intentar usar fecha_pago
                if (ingreso.fecha_pago) {
                    try {
                        const fecha = new Date(ingreso.fecha_pago);
                        const mes = fecha.getMonth() + 1;
                        const anio = fecha.getFullYear();
                        
                        if (anio === anioSeleccionado) {
                            mesesSet.add(`${mes}-${anioSeleccionado}`);
                        }
                    } catch (error) {
                        // Si hay un error al parsear la fecha, usar mes_pagado y anio_pagado
                        if (ingreso.mes_pagado && ingreso.anio_pagado === anioSeleccionado) {
                            mesesSet.add(`${ingreso.mes_pagado}-${anioSeleccionado}`);
                        }
                    }
                } 
                // Si no hay fecha_pago, usar mes_pagado y anio_pagado
                else if (ingreso.mes_pagado && ingreso.anio_pagado === anioSeleccionado) {
                    mesesSet.add(`${ingreso.mes_pagado}-${anioSeleccionado}`);
                }
            });
            
            egresos.forEach(egreso => {
                const fecha = new Date(egreso.fecha_pago);
                const mes = fecha.getMonth() + 1;
                const anio = fecha.getFullYear();
                
                if (anio === anioSeleccionado) {
                    mesesSet.add(`${mes}-${anioSeleccionado}`);
                }
            });
            
            actualizarMesesDisponibles(mesesSet, anioSeleccionado);
        }
    }, [anioSeleccionado, ingresos, egresos, loading]);

    // Función para filtrar datos según el período seleccionado
    const filtrarDatosPorPeriodo = () => {
        if (tipoPeriodo === 'mes') {
            return {
                ingresos: ingresos.filter(i => {
                    // Comprobar si existe fecha_pago
                    if (i.fecha_pago) {
                        const fecha = new Date(i.fecha_pago);
                        return fecha.getMonth() + 1 === mesSeleccionado && fecha.getFullYear() === anioSeleccionado;
                    } 
                    // Si no hay fecha_pago o hay un error al parsearla, usar mes_pagado y anio_pagado como respaldo
                    else if (i.mes_pagado && i.anio_pagado) {
                        return i.mes_pagado === mesSeleccionado && i.anio_pagado === anioSeleccionado;
                    }
                    return false;
                }),
                egresos: egresos.filter(e => {
                    // Verificar si existe la propiedad fecha
                    if (e.fecha) {
                        const fecha = new Date(e.fecha);
                        return fecha.getMonth() + 1 === mesSeleccionado && fecha.getFullYear() === anioSeleccionado;
                    }
                    // Si no hay fecha pero hay fecha_pago como alternativa
                    else if (e.fecha_pago) {
                        const fecha = new Date(e.fecha_pago);
                        return fecha.getMonth() + 1 === mesSeleccionado && fecha.getFullYear() === anioSeleccionado;
                    }
                    return false;
                })
            };
        } else { // Por año
            return {
                ingresos: ingresos.filter(i => {
                    // Comprobar si existe fecha_pago
                    if (i.fecha_pago) {
                        const fecha = new Date(i.fecha_pago);
                        return fecha.getFullYear() === anioSeleccionado;
                    } 
                    // Si no hay fecha_pago o hay un error al parsearla, usar anio_pagado como respaldo
                    else if (i.anio_pagado) {
                        return i.anio_pagado === anioSeleccionado;
                    }
                    return false;
                }),
                egresos: egresos.filter(e => {
                    // Verificar si existe la propiedad fecha
                    if (e.fecha) {
                        const fecha = new Date(e.fecha);
                        return fecha.getFullYear() === anioSeleccionado;
                    }
                    // Si no hay fecha pero hay fecha_pago como alternativa
                    else if (e.fecha_pago) {
                        const fecha = new Date(e.fecha_pago);
                        return fecha.getFullYear() === anioSeleccionado;
                    }
                    return false;
                })
            };
        }
    };

    // Datos filtrados según el período
    const datosFiltrados = filtrarDatosPorPeriodo();

    // Calcular resumen según los datos filtrados
    const resumenPeriodo = {
        totalIngresos: datosFiltrados.ingresos.reduce((sum, i) => sum + parseFloat(i.monto_pagado), 0),
        totalEgresos: datosFiltrados.egresos.reduce((sum, e) => sum + parseFloat(e.monto), 0),
        distribucionIngresos: datosFiltrados.ingresos.reduce((acc, i) => {
            acc[i.tipo_pago] = (acc[i.tipo_pago] || 0) + parseFloat(i.monto_pagado);
            return acc;
        }, {}),
        distribucionEgresos: datosFiltrados.egresos.reduce((acc, e) => {
            acc[e.tipo_egreso] = (acc[e.tipo_egreso] || 0) + parseFloat(e.monto);
            return acc;
        }, {})
    };
    
    // Verificar si hay datos para mostrar
    const hayDatosIngresos = datosFiltrados.ingresos.length > 0;
    const hayDatosEgresos = datosFiltrados.egresos.length > 0;
    const hayDatos = hayDatosIngresos || hayDatosEgresos;

    // Para datos mensuales por año (cuando se selecciona año completo)
    const datosMensualesPorAnio = {
        ingresos: Array(12).fill(0).map((_, i) => {
            return ingresos
                .filter(ing => {
                    // Comprobar si existe fecha_pago
                    if (ing.fecha_pago) {
                        const fecha = new Date(ing.fecha_pago);
                        return fecha.getMonth() === i && fecha.getFullYear() === anioSeleccionado;
                    } 
                    // Si no hay fecha_pago o hay un error al parsearla, usar mes_pagado y anio_pagado como respaldo
                    else if (ing.mes_pagado && ing.anio_pagado) {
                        return ing.mes_pagado === (i + 1) && ing.anio_pagado === anioSeleccionado;
                    }
                    return false;
                })
                .reduce((sum, ing) => sum + parseFloat(ing.monto_pagado), 0);
        }),
        egresos: Array(12).fill(0).map((_, i) => {
            return egresos
                .filter(egr => {
                    const fecha = new Date(egr.fecha_pago);
                    return fecha.getMonth() === i && fecha.getFullYear() === anioSeleccionado;
                })
                .reduce((sum, egr) => sum + parseFloat(egr.monto), 0);
        })
    };

    const egresosPendientes = egresos.filter(e => e.estado === 'Pendiente');

    const mesesNombres = {
        1: 'Enero', 2: 'Febrero', 3: 'Marzo', 4: 'Abril',
        5: 'Mayo', 6: 'Junio', 7: 'Julio', 8: 'Agosto',
        9: 'Septiembre', 10: 'Octubre', 11: 'Noviembre', 12: 'Diciembre'
    };

    // Función para reiniciar las gráficas cuando se cambia de vista
    const resetCharts = () => {
        // Destruir instancias de gráficas existentes para evitar bugs
        ChartJS.unregister();
        ChartJS.register(
            CategoryScale,
            LinearScale,
            PointElement,
            LineElement,
            BarElement,
            ArcElement,
            Title,
            Tooltip,
            Legend
        );
    };

    // Efecto para reiniciar las gráficas cuando cambia el tipo de período
    useEffect(() => {
        resetCharts();
    }, [tipoPeriodo]);

    // Datos para el reporte PDF
    const reportData = {
        resumenMensual: resumenPeriodo,
        egresosPendientes,
        mesSeleccionado: tipoPeriodo === 'mes' ? mesesNombres[mesSeleccionado] : 'Año completo',
        anioSeleccionado,
        tipoPeriodo
    };

    // Opciones mejoradas para los gráficos
    const commonChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) {
                            label += ': ';
                        }
                        label += '$ ' + context.raw.toFixed(2);
                        return label;
                    }
                },
                padding: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                titleColor: '#1E293B',
                bodyColor: '#1E293B',
                borderColor: '#E2E8F0',
                borderWidth: 1,
                displayColors: true,
                boxPadding: 3
            },
            // Por defecto, no mostrar título en las opciones comunes
            title: {
                display: false
            }
        }
    };

    // Mapeo de tipos de pago a nombres más descriptivos
    const tiposPagoMapeados = {
        'Efectivo': 'Efectivo',
        'Transferencia': 'Transferencia',
        'Cheque': 'Cheque',
        'Tarjeta': 'Tarjeta de Crédito/Débito',
        'Otro': 'Otro Método'
    };

    // Gráfico de distribución de ingresos con etiquetas mejoradas
    const ingresosChartData = {
        labels: Object.keys(resumenPeriodo.distribucionIngresos).map(key => tiposPagoMapeados[key] || key),
        datasets: [
            {
                label: 'Ingresos por Tipo',
                data: Object.values(resumenPeriodo.distribucionIngresos),
                backgroundColor: [
                    'rgba(16, 185, 129, 0.7)',  // Verde esmeralda
                    'rgba(59, 130, 246, 0.7)',  // Azul
                    'rgba(139, 92, 246, 0.7)',  // Violeta
                    'rgba(14, 165, 233, 0.7)',  // Celeste
                    'rgba(20, 184, 166, 0.7)',  // Verde azulado
                ],
                borderColor: [
                    'rgb(16, 185, 129)',
                    'rgb(59, 130, 246)',
                    'rgb(139, 92, 246)',
                    'rgb(14, 165, 233)',
                    'rgb(20, 184, 166)',
                ],
                borderWidth: 1
            }
        ]
    };

    const ingresosChartOptions = {
        ...commonChartOptions,
        indexAxis: 'y',  // Hace que el gráfico sea horizontal
        plugins: {
            ...commonChartOptions.plugins,
            legend: {
                display: false  // Ocultar leyenda ya que las etiquetas están en el eje Y
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return 'Monto: $ ' + context.raw.toFixed(2);
                    }
                }
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    drawBorder: true,
                    color: 'rgba(226, 232, 240, 0.5)'
                },
                ticks: {
                    callback: function(value) {
                        return '$ ' + value.toFixed(2);
                    }
                }
            },
            y: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        weight: 'bold'
                    }
                }
            }
        }
    };

    // Mapeo de tipos de egreso a nombres más descriptivos
    const tiposEgresoMapeados = {
        'Servicios': 'Servicios',
        'Materiales': 'Materiales',
        'Salarios': 'Salarios',
        'Otros': 'Otros Gastos'
    };

    // Gráfico de distribución de egresos con etiquetas mejoradas
    const egresosChartData = {
        labels: Object.keys(resumenPeriodo.distribucionEgresos).map(key => tiposEgresoMapeados[key] || key),
        datasets: [
            {
                data: Object.values(resumenPeriodo.distribucionEgresos),
                backgroundColor: [
                    'rgba(239, 68, 68, 0.7)',   // Rojo
                    'rgba(249, 115, 22, 0.7)',  // Naranja
                    'rgba(234, 179, 8, 0.7)',   // Amarillo
                    'rgba(217, 119, 6, 0.7)',   // Ámbar
                    'rgba(220, 38, 38, 0.7)',   // Rojo oscuro
                ],
                borderColor: [
                    'rgb(239, 68, 68)',
                    'rgb(249, 115, 22)',
                    'rgb(234, 179, 8)',
                    'rgb(217, 119, 6)',
                    'rgb(220, 38, 38)',
                ],
                borderWidth: 1
            }
        ]
    };

    const egresosChartOptions = {
        ...commonChartOptions,
        cutout: '60%',  // Para el gráfico de donut
        plugins: {
            ...commonChartOptions.plugins,
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.label || '';
                        const value = context.raw;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: $ ${value.toFixed(2)} (${percentage}%)`;
                    }
                }
            }
        }
    };

    // Gráfico comparativo de ingresos vs egresos (se mantiene como gráfico de barras pero con mejoras)
    const comparativaChartData = {
        labels: ['Ingresos', 'Egresos'],
        datasets: [
            {
                label: 'Monto Total',
                data: [resumenPeriodo.totalIngresos, resumenPeriodo.totalEgresos],
                backgroundColor: ['rgba(34, 197, 94, 0.8)', 'rgba(239, 68, 68, 0.8)'],
                borderColor: ['rgb(34, 197, 94)', 'rgb(239, 68, 68)'],
                borderWidth: 1,
                borderRadius: 6,
                barThickness: 80
            }
        ]
    };

    const comparativaChartOptions = {
        ...commonChartOptions,
        plugins: {
            ...commonChartOptions.plugins,
            legend: {
                display: false  // Ocultar leyenda ya que las etiquetas están en el eje X
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        const label = context.dataset.label || '';
                        return `${label}: $ ${context.raw.toFixed(2)}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    display: true,
                    drawBorder: true,
                    color: 'rgba(226, 232, 240, 0.5)'
                },
                ticks: {
                    callback: function(value) {
                        return '$ ' + value.toFixed(2);
                    }
                }
            },
            x: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        weight: 'bold'
                    }
                }
            }
        },
        // Reiniciar el estado de la gráfica al cambiar de vista
        animation: {
            duration: 0 // Desactivar animaciones para evitar problemas al cambiar de vista
        },
        maintainAspectRatio: false,
        responsive: true
    };

    // Gráfico de tendencia mensual por año
    const tendenciaAnualChartData = {
        labels: Object.values(mesesNombres),
        datasets: [
            {
                label: 'Ingresos',
                data: datosMensualesPorAnio.ingresos,
                borderColor: 'rgb(34, 197, 94)',
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                borderWidth: 2,
                fill: true,
                tension: 0.4
            },
            {
                label: 'Egresos',
                data: datosMensualesPorAnio.egresos,
                borderColor: 'rgb(239, 68, 68)',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                borderWidth: 2,
                fill: true,
                tension: 0.4
            }
        ]
    };

    const tendenciaAnualChartOptions = {
        ...commonChartOptions,
        plugins: {
            ...commonChartOptions.plugins
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: {
                    display: true,
                    drawBorder: true,
                    color: 'rgba(226, 232, 240, 0.5)'
                },
                ticks: {
                    callback: function(value) {
                        return '$ ' + value.toFixed(2);
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    const generarReporte = async () => {
        try {
            // ... existing report generation logic ...
            showSuccessToast('Reporte generado exitosamente');
        } catch (error) {
            showErrorToast('Error al generar el reporte: ' + error.message);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 p-2 sm:p-6">
            <ToastContainer 
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                className="mt-16"
                style={{ zIndex: 9999 }}
            />
            <div className="max-w-7xl mx-auto">
                {/* Encabezado y botón de descarga */}
                <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-4 sm:mb-6">
                    <div className="p-4 sm:p-6 border-b border-gray-100">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <h1 className="text-xl sm:text-2xl font-semibold text-gray-800 flex items-center">
                                    <FileText className="mr-3 text-indigo-500" size={24} />
                                    Reportes Financieros
                                </h1>
                                <p className="text-gray-500 text-sm mt-1">
                                    Visualiza y descarga reportes financieros
                                </p>
                            </div>
                            <PDFDownloadLink
                                document={<ReportePDF data={reportData} />}
                                fileName={`reporte-financiero-${tipoPeriodo === 'mes' ? mesesNombres[mesSeleccionado] + '-' : ''}${anioSeleccionado}.pdf`}
                                className="bg-indigo-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-indigo-600 transition-colors"
                            >
                                <Download size={20} />
                                Descargar PDF
                            </PDFDownloadLink>
                        </div>

                        {/* Selector de período */}
                        <div className="mt-4">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="relative w-full sm:w-auto">
                                    <div className="flex flex-col gap-2">
                                        <label className="text-sm text-gray-600 font-medium">Tipo de reporte</label>
                                        <div className="w-full flex bg-gray-100 rounded-lg p-1">
                                            <button
                                                onClick={() => {
                                                    setTipoPeriodo('mes');
                                                    // Esperar a que el estado se actualice antes de reiniciar las gráficas
                                                    setTimeout(resetCharts, 0);
                                                }}
                                                className={`px-4 py-2.5 text-sm rounded-lg transition-all duration-200 flex items-center justify-center ${tipoPeriodo === 'mes' 
                                                    ? 'bg-white text-indigo-600 font-medium shadow-sm' 
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                } flex-1`}
                                            >
                                                <span className="flex items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    Mensual
                                                </span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setTipoPeriodo('anio');
                                                    // Esperar a que el estado se actualice antes de reiniciar las gráficas
                                                    setTimeout(resetCharts, 0);
                                                }}
                                                className={`px-4 py-2.5 text-sm rounded-lg transition-all duration-200 flex items-center justify-center ${tipoPeriodo === 'anio' 
                                                    ? 'bg-white text-indigo-600 font-medium shadow-sm' 
                                                    : 'text-gray-700 hover:bg-gray-50'
                                                } flex-1`}
                                            >
                                                <span className="flex items-center">
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                    </svg>
                                                    Anual
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4">
                                    {tipoPeriodo === 'mes' && (
                                        <div className="relative">
                                            <label className="text-sm text-gray-600 font-medium block mb-2">Mes</label>
                                            <div className="relative">
                                <select
                                    value={mesSeleccionado}
                                    onChange={(e) => setMesSeleccionado(parseInt(e.target.value))}
                                                    className="appearance-none w-full sm:w-auto min-w-[160px] rounded-lg border border-gray-300 bg-white py-2.5 pl-4 pr-10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                >
                                    {mesesDisponibles.length > 0 ? (
                                        mesesDisponibles.map(mes => (
                                            <option key={mes} value={mes}>{mesesNombres[mes]}</option>
                                        ))
                                    ) : (
                                        <option value="">No hay meses con datos</option>
                                    )}
                                </select>
                                                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
                                                    <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="relative">
                                        <label className="text-sm text-gray-600 font-medium block mb-2">Año</label>
                                        <div className="relative">
                                <select
                                    value={anioSeleccionado}
                                    onChange={(e) => setAnioSeleccionado(parseInt(e.target.value))}
                                                className="appearance-none w-full sm:w-auto min-w-[120px] rounded-lg border border-gray-300 bg-white py-2.5 pl-4 pr-10 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                                >
                                    {aniosDisponibles.length > 0 ? (
                                        aniosDisponibles.map(anio => (
                                            <option key={anio} value={anio}>{anio}</option>
                                        ))
                                    ) : (
                                        <option value="">No hay años con datos</option>
                                    )}
                                </select>
                                            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-600">
                                                <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                                    <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path>
                                                </svg>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tarjetas de resumen - Vista móvil */}
                <div className="block sm:hidden space-y-4">
                    {/* Balance General */}
                    <div className="bg-white p-4 rounded-xl shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-medium text-gray-900">Balance General</h3>
                            <DollarSign className="text-green-500" size={20} />
                        </div>
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Total Ingresos</span>
                                <span className="text-sm text-green-600 font-medium">
                                    ${resumenPeriodo.totalIngresos.toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Total Egresos</span>
                                <span className="text-sm text-rose-600 font-medium">
                                    ${resumenPeriodo.totalEgresos.toFixed(2)}
                                </span>
                            </div>
                            <div className="pt-2 border-t">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium">Balance</span>
                                    <span className={`text-sm font-medium ${resumenPeriodo.totalIngresos - resumenPeriodo.totalEgresos >= 0 ? 'text-green-600' : 'text-rose-600'}`}>
                                        ${(resumenPeriodo.totalIngresos - resumenPeriodo.totalEgresos).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>



                    {/* Gráficas en móvil */}
                    <div className="space-y-4">
                        {!hayDatos ? (
                            <div className="bg-white p-4 rounded-xl shadow-sm">
                                <div className="text-center py-8">
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay datos financieros disponibles</h3>
                                    <p className="text-gray-500">
                                        {tipoPeriodo === 'mes' 
                                            ? `No existen registros de ingresos ni egresos para ${mesesNombres[mesSeleccionado]} de ${anioSeleccionado}.` 
                                            : `No existen registros de ingresos ni egresos para el año ${anioSeleccionado}.`}
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <>
                                {tipoPeriodo === 'anio' && (
                                    <div className="bg-white p-4 rounded-xl shadow-sm">
                                        <h3 className="text-base font-medium text-gray-900 mb-3">Tendencia Anual</h3>
                                        <div style={{ height: '220px' }}>
                                            <Line data={tendenciaAnualChartData} options={tendenciaAnualChartOptions} />
                                        </div>
                                    </div>
                                )}
                                
                                <div className="bg-white p-4 rounded-xl shadow-sm">
                                    <h3 className="text-base font-medium text-gray-900 mb-3">Distribución de Egresos</h3>
                                    <div style={{ height: '220px' }}>
                                        {hayDatosEgresos ? (
                                            <Doughnut data={egresosChartData} options={egresosChartOptions} />
                                        ) : (
                                            <div className="flex items-center justify-center h-full">
                                                <p className="text-gray-500">No existen egresos registrados para este período</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white p-4 rounded-xl shadow-sm">
                                    <h3 className="text-base font-medium text-gray-900 mb-3">Ingresos por Categoría</h3>
                                    <div style={{ height: '220px' }}>
                                        {hayDatosIngresos ? (
                                            <Bar data={ingresosChartData} options={ingresosChartOptions} />
                                        ) : (
                                            <div className="flex items-center justify-center h-full">
                                                <p className="text-gray-500">No existen ingresos registrados para este período</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Egresos Pendientes en móvil */}
                    <div className="bg-white rounded-xl shadow-sm p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-base font-medium text-gray-900">Egresos Pendientes</h3>
                            <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                {egresosPendientes.length} pendientes
                            </span>
                        </div>
                        <div className="space-y-3">
                            {egresosPendientes.length > 0 ? (
                                egresosPendientes.map((egreso) => (
                                    <div key={egreso.id_egreso} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <p className="text-sm font-medium text-gray-900">{egreso.concepto}</p>
                                                <p className="text-xs text-gray-500 mt-1">{egreso.tipo_egreso}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-medium text-rose-600">${parseFloat(egreso.monto).toFixed(2)}</p>
                                                <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Pendiente
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-2">
                                    <span className="text-sm text-gray-500">No hay egresos pendientes</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Vista desktop - mantener el código existente */}
                <div className="hidden sm:block">
                    {/* Grid de tarjetas de resumen */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                        {/* Balance General */}
                        <div className="bg-white p-6 rounded-xl shadow-sm lg:col-span-3 md:col-span-2 md:mx-auto lg:mx-0">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Balance General</h3>
                                <DollarSign className="text-green-500" size={24} />
                            </div>
                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Total Ingresos</span>
                                    <span className="text-green-600 font-medium">
                                        ${resumenPeriodo.totalIngresos.toFixed(2)}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Total Egresos</span>
                                    <span className="text-rose-600 font-medium">
                                        ${resumenPeriodo.totalEgresos.toFixed(2)}
                                    </span>
                                </div>
                                <div className="pt-2 border-t">
                                    <div className="flex justify-between items-center">
                                        <span className="font-medium">Balance</span>
                                        <span className={`font-medium ${resumenPeriodo.totalIngresos - resumenPeriodo.totalEgresos >= 0 ? 'text-green-600' : 'text-rose-600'}`}>
                                            ${(resumenPeriodo.totalIngresos - resumenPeriodo.totalEgresos).toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Gráficas */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {!hayDatos ? (
                            <div className="bg-white p-6 rounded-xl shadow-sm lg:col-span-2 h-[400px] flex flex-col items-center justify-center">
                                <div className="text-center p-8">
                                    <h3 className="text-xl font-medium text-gray-900 mb-2">No hay datos financieros disponibles</h3>
                                    <p className="text-gray-500">
                                        {tipoPeriodo === 'mes' 
                                            ? `No existen registros de ingresos ni egresos para ${mesesNombres[mesSeleccionado]} de ${anioSeleccionado}.` 
                                            : `No existen registros de ingresos ni egresos para el año ${anioSeleccionado}.`}
                                    </p>
                                </div>
                            </div>
                        ) : tipoPeriodo === 'anio' ? (
                            <>
                                <div className="bg-white p-6 rounded-xl shadow-sm lg:col-span-2 h-[400px] flex flex-col">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Tendencia de Ingresos y Egresos por Mes en {anioSeleccionado}</h3>
                                    <div className="flex-1 flex items-center justify-center">
                                        <Line data={tendenciaAnualChartData} options={tendenciaAnualChartOptions} />
                                    </div>
                                </div>
                                
                                <div className="bg-white p-6 rounded-xl shadow-sm h-[400px] flex flex-col">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Distribución de Egresos</h3>
                                    <div className="flex-1 flex items-center justify-center">
                                        <Doughnut data={egresosChartData} options={egresosChartOptions} />
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-sm h-[400px] flex flex-col">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Ingresos por Categoría</h3>
                                    <div className="flex-1 flex items-center justify-center">
                                        <Bar data={ingresosChartData} options={ingresosChartOptions} />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="bg-white p-6 rounded-xl shadow-sm h-[400px] flex flex-col">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Distribución de Ingresos</h3>
                                    <div className="flex-1 flex items-center justify-center">
                                        {hayDatosIngresos ? (
                                            <Bar data={ingresosChartData} options={ingresosChartOptions} />
                                        ) : (
                                            <div className="text-center p-4">
                                                <p className="text-gray-500">No existen ingresos registrados para este período</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-sm h-[400px] flex flex-col">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Distribución de Egresos</h3>
                                    <div className="flex-1 flex items-center justify-center">
                                        {hayDatosEgresos ? (
                                            <Doughnut data={egresosChartData} options={egresosChartOptions} />
                                        ) : (
                                            <div className="text-center p-4">
                                                <p className="text-gray-500">No existen egresos registrados para este período</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-white p-6 rounded-xl shadow-sm h-[400px] flex flex-col">
                                    <h3 className="text-lg font-medium text-gray-900 mb-4">Comparativa Ingresos vs Egresos</h3>
                                    <div className="flex-1 flex items-center justify-center">
                                        {hayDatos ? (
                                            <Bar 
                                                key={`comparativa-chart-${tipoPeriodo}-${mesSeleccionado}-${anioSeleccionado}`}
                                                data={comparativaChartData}
                                                options={comparativaChartOptions}
                                            />
                                        ) : (
                                            <div className="text-center p-4">
                                                <p className="text-gray-500">No hay datos para comparar en este período</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </> 
                        )}
                    </div>

                    {/* Egresos Pendientes */}
                    <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Egresos Pendientes</h3>
                            <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                {egresosPendientes.length} pendientes
                            </span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Concepto</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monto</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {egresosPendientes.map((egreso) => (
                                        <tr key={egreso.id_egreso} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{egreso.concepto}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${parseFloat(egreso.monto).toFixed(2)}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{egreso.tipo_egreso}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                                    Pendiente
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportesFinanzas;
